import { z } from "zod";

/**
 * Shared DTO cho DANH TÍNH CÔNG KHAI của user hiện tại — GET /v1/users/me/identity
 * (US-IDM.F2-0289-S03 / @UC9511 — màn "Me": mã định danh + deep link + QR payload).
 *
 * Một nguồn sự thật cho api (shape response) và mobile (gọi + map), tránh drift (DRY).
 *
 * Cross-check: path canonical `/v1/users/me/identity` (@Controller("users/me/identity") +
 * global prefix v1) — CÙNG họ với /v1/users/me/groups (0019). KHÁC /v1/me (profile 0014).
 *
 * Bảo mật (C2/C3): SELF-ONLY — userId LẤY TỪ token, KHÔNG nhận id từ query/body (chống IDOR).
 * Response KHÔNG mang email/phone/PII — publicHandle là mã tra cứu ASCII, KHÔNG bí mật.
 *
 * publicHandle NULLABLE: user thuộc nhóm standard (Auto) chưa từng cần handle ⇒ chưa gen (LAZY).
 * Khi null ⇒ deepLink/qrPayload cũng null (KHÔNG dựng link rỗng), `hasHandle=false` báo trạng
 * thái rõ ràng cho UI (KHÔNG lỗi — chưa có handle là trạng thái hợp lệ, không phải exception).
 */

/**
 * Base deep link cho danh tính công khai — hằng SẢN PHẨM (product constant), KHÔNG cấu hình runtime.
 * Format: `https://link.goppy.vn/u/<publicHandle>` — mở app / web resolve về đúng user qua handle.
 * ASCII-safe (handle đã slug hoá) ⇒ nhúng QR / URL an toàn.
 */
export const IDENTITY_DEEP_LINK_BASE = "https://link.goppy.vn/u/" as const;

/** Dựng deep link từ handle (DRY api ↔ mobile). null in → null out (KHÔNG dựng link rỗng). */
export function buildIdentityDeepLink(publicHandle: string | null): string | null {
  if (!publicHandle) return null;
  return `${IDENTITY_DEEP_LINK_BASE}${publicHandle}`;
}

/**
 * US-IDM.F2-0289-S04 (@UC9512) — GIỚI HẠN độ dài `q` lookup ở biên. Handle = slug(tên ≤60) + '-' +
 * shortId (8) ⇒ tối đa ~70 char; deepLink thêm base ~24 char. 256 dư dả cho mọi biến thể hợp lệ,
 * chặn payload rác dài (DoS/regex-abuse) TRƯỚC khi vào service.
 */
export const USER_LOOKUP_QUERY_MAX_LENGTH = 256;

/**
 * Ký tự hợp lệ của publicHandle (khớp buildPublicHandle: [a-z0-9] + '-'). Dùng để lọc kết quả
 * trích từ deepLink/QR — sau khi tách handle, phải khớp mẫu này thì mới coi là handle hợp lệ.
 */
const PUBLIC_HANDLE_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/**
 * US-IDM.F2-0289-S04 (@UC9512) — trích publicHandle từ `q` thô (raw handle | deepLink | qrPayload).
 *
 * `q` có thể là:
 *  - handle thuần: `anh-vinh-ph-lop-10a1-usda123da`
 *  - deep link đầy đủ: `https://link.goppy.vn/u/anh-vinh-...`
 *  - qrPayload (= deepLink cùng chuỗi, S03)
 *  - biến thể không scheme / có query-string / có trailing slash: `link.goppy.vn/u/anh-vinh-...?x=1`
 *
 * NGUYÊN TẮC (THUẦN, KHÔNG side-effect, KHÔNG throw):
 *  - Chuẩn hoá: trim + lowercase (handle vốn ASCII-lowercase; case-insensitive input thân thiện).
 *  - Nếu chứa segment `/u/` (dạng link/QR) ⇒ lấy phần SAU cùng, cắt tại '/' , '?' , '#' đầu tiên.
 *  - Ngược lại coi cả chuỗi là handle thuần.
 *  - Kết quả phải khớp PUBLIC_HANDLE_PATTERN; không khớp / rỗng ⇒ null (KHÔNG truy vấn rác).
 *  Trả handle chuẩn hoá (để service findUnique theo @unique) hoặc null.
 */
export function extractHandleFromLookupQuery(q: string): string | null {
  const raw = (q ?? "").trim().toLowerCase();
  if (raw === "") return null;

  // Tách phần định danh: nếu là link/QR có '/u/', lấy phần SAU marker cuối cùng.
  const marker = "/u/";
  const markerIdx = raw.lastIndexOf(marker);
  let candidate = markerIdx === -1 ? raw : raw.slice(markerIdx + marker.length);

  // Cắt tại ranh giới path/query/fragment đầu tiên (link có thể kèm `/`, `?`, `#`).
  candidate = candidate.split(/[/?#]/, 1)[0] ?? "";

  return PUBLIC_HANDLE_PATTERN.test(candidate) ? candidate : null;
}

/**
 * Shape response cho GET /v1/users/me/identity.
 * - publicHandle: mã định danh ASCII (slug(tên)+'-'+shortId) hoặc null (chưa gen — nhóm standard).
 * - deepLink: `https://link.goppy.vn/u/<handle>` hoặc null (khi chưa có handle).
 * - qrPayload: payload nhúng QR = deepLink (cùng chuỗi) hoặc null. Tách field để mobile không phải
 *   suy diễn (QR có thể khác deepLink ở phiên bản sau; hiện tại = deepLink).
 * - hasHandle: cờ trạng thái rõ ràng (true khi đã có handle). UI hiển thị CTA "tạo mã" khi false.
 */
export const meIdentityResponseSchema = z.object({
  publicHandle: z.string().nullable(),
  deepLink: z.string().url().nullable(),
  qrPayload: z.string().url().nullable(),
  hasHandle: z.boolean(),
});

export type MeIdentityResponse = z.infer<typeof meIdentityResponseSchema>;

/**
 * US-IDM.F2-0289-S04 (@UC9512) — GET /v1/users/lookup?q= (auth, MỞ CHUNG mọi user đăng nhập).
 *
 * Query: `q` = handle | deepLink | qrPayload. Validate biên: bắt buộc, non-empty sau trim, ≤ max.
 * Service tự trích handle qua extractHandleFromLookupQuery (không ép client biết format).
 */
export const userLookupQuerySchema = z.object({
  q: z.string().trim().min(1).max(USER_LOOKUP_QUERY_MAX_LENGTH),
});

export type UserLookupQuery = z.infer<typeof userLookupQuerySchema>;

/**
 * Response TỐI GIẢN cho lookup — CHỈ danh tính công khai để trưởng nhóm chọn đúng người khi map.
 *
 * Bảo mật (C2/C3 — PII): TUYỆT ĐỐI KHÔNG email/phone. publicHandle là mã tra cứu ASCII công khai
 * (KHÔNG bí mật). displayName = fullName (tên hiển thị người dùng chủ động đặt — không nhạy cảm như
 * email/SĐT). avatarUrl công khai (ảnh đại diện). Không lộ id nội bộ nào khác, không status/role.
 */
export const userLookupResponseSchema = z.object({
  userId: z.string().uuid(),
  publicHandle: z.string(),
  displayName: z.string().nullable(),
  avatarUrl: z.string().nullable(),
});

export type UserLookupResponse = z.infer<typeof userLookupResponseSchema>;
